// @ts-nocheck

import { useCallback, useEffect } from 'react';
import { D3GraphSetting } from 'src/enums/D3GraphSetting';
import * as d3 from 'd3';

const OrganizationalChart = ({ data }: {
  data: {
    rootNode: string,
    owners: {
      is_ubo: boolean,
      level: number,
      name: string,
      share: number,
    }[],
    graph: {
      source: string,
      target: string,
      ownership: number,
    }[],
  };
}) => {
  // This is required to track the actual first load for the component
  // useState is not used here since an error is received on node drag for some reason
  // And it goes into an infinite state of toggling between true and false
  let initialLoad = true;
  const setInitialLoad = useCallback((value: boolean) => {
    initialLoad = value;
  }, []);

  const createLinks = useCallback((svgGroup) => {
    const link = svgGroup.selectAll('.link')
      .data(data.graph, d => `${d.source}-${d.target}`)
      .enter()
      .append('g')
      .attr('class', 'link');

    // Append the path for the link
    link.append('path')
      .attr('class', 'link-path')
      .attr('fill', 'none')
      .attr('stroke', '#ccc')
      .attr('stroke-width', 1)
      .attr('marker-start', 'url(#arrowhead)'); // Arrows are always from the target to the source in the case

    // Append rectangle for link text background
    link.append('rect')
      .attr('class', 'link-rect')
      .attr('width', 50)
      .attr('height', 18)
      .attr('fill', d => {
        // We do not want to show rectangles for self ownership
        // since they are to the side of the link unlike the others which are centered
        // as well as if they are in the unknown case (nan)
        if (d.source.name === d.target.name || d.ownership === 'nan') {
          return 'none';
        }

        return 'white';
      });

    // Append text to each link showing the percentage
    link.append('text')
      .attr('class', 'link-text')
      .attr('text-anchor', 'middle')
      .text((d) => {
        // Hide the text for the unknown ownership case (nan)
        if (d.ownership === 'nan') return;

        return Number(d.ownership).toFixed(2) + '%';
      });
  }, [data.graph]);

  const createNodes = useCallback((svgGroup, rootNode) => {
    const node = svgGroup.selectAll('.node')
      .data(data.owners, d => d.name)
      .enter()
      .append('g')
      .attr('class', 'node')
      // Add a fixed y for the different levels
      .attr('fy', (d) => {
        if (d.level > 0) {
          d.fy = D3GraphSetting.svgHeight - ((D3GraphSetting.nodeHeight + D3GraphSetting.nodeWidth + D3GraphSetting.nodeSpacing) * d.level);
        }
      });

    // Adds full name display on node hover
    node.append('title')
      .text((d) => d.name);

    // Add rectangles for each node
    node.append('rect')
      .attr('width', D3GraphSetting.nodeWidth)
      .attr('height', D3GraphSetting.nodeHeight)
      .attr('x', -D3GraphSetting.nodeWidth / 2)
      .attr('y', -D3GraphSetting.nodeHeight / 2)
      .attr('stroke', D3GraphSetting.strokeColor)
      .attr('fill', function (d) { return d.name === rootNode ? D3GraphSetting.mainCardFillColor : D3GraphSetting.fillColor; })
      .attr('rx', D3GraphSetting.nodeBorderRadius)
      .attr('ry', D3GraphSetting.nodeBorderRadius);

    // Add labels for each node
    node.append('text')
      .attr('dominant-baseline', 'middle')
      .attr('x', (-D3GraphSetting.nodeWidth / 2) + D3GraphSetting.iconSize + D3GraphSetting.elementPadding * 2)
      .text(d => d.name)
      .style('fill', function (d) { return d.name === rootNode ? D3GraphSetting.mainCardTextColor : D3GraphSetting.textColor; })
      .text(function (d) {
        let cardText = d.name;

        if (cardText.length > D3GraphSetting.textCharacterLimit) cardText = cardText.slice(0, D3GraphSetting.textCharacterLimit) + '...';

        return cardText;
      });

    // Add an icon
    node.append('svg:image')
      .attr('xlink:href', (d) => {
        if (d.type === 'hidden') return '/icons/icon-hidden.svg';

        if (!d.is_ubo) return '/icons/icon-business.svg';

        return '/icons/icon-individual.svg';
      })
      .attr('width', D3GraphSetting.iconSize)
      .attr('height', D3GraphSetting.iconSize)
      .attr('x', -(D3GraphSetting.nodeWidth / 2) + D3GraphSetting.elementPadding)
      .attr('y', -(D3GraphSetting.iconSize / 2));

    // Add ownership percent for each node
    node.append('text')
      .attr('dominant-baseline', 'middle')
      .attr('text-anchor', 'end')
      .attr('x', (D3GraphSetting.nodeWidth / 2) - D3GraphSetting.elementPadding)
      .style('fill', function (d) { return d.name === rootNode ? D3GraphSetting.mainCardTextColor : D3GraphSetting.textColor; })
      .text(function (d) {
        // Display text with dashes for the unknown ownership case
        if (d.share === 'unknown') return '--%';

        return d.share ? (d.share * 100).toFixed(2) + '%' : null;
      });
  }, [data.owners]);

  const updateContainerSize = useCallback((svg, svgGroup, currentTransform) => {
    // On initial load, we want everything to be calculated, scaled and positioned
    // Everything after that is accurate and does not require these steps
    if (initialLoad) {
      const containerSize = svg.node().getBoundingClientRect();
      const groupSize = svgGroup.node().getBBox();

      const scaleFactorWidth = containerSize.width / (groupSize.width + D3GraphSetting.elementPadding * 2);
      const scaleFactorHeight = containerSize.height / (groupSize.height + D3GraphSetting.elementPadding * 2);
      const scaleFactor = Math.min(scaleFactorWidth, scaleFactorHeight);

      const groupX = (-groupSize.x + D3GraphSetting.elementPadding) * scaleFactor;
      const groupY = (-groupSize.y + D3GraphSetting.elementPadding) * scaleFactor;

      currentTransform.x = groupX;
      currentTransform.y = groupY;
      currentTransform.k = scaleFactor;
    }

    // Apply the current transform
    svgGroup.attr('transform', currentTransform);
  }, [initialLoad]);

  const fixNodePositions = (nodes) => {
    nodes.forEach(node => {
      node.fx = node.x;
      node.fy = node.y;
    });
  };

  useEffect(() => {
    setInitialLoad(true);

    const rootNode = data.rootNode;

    // Create a force simulation
    const simulation = d3.forceSimulation(data.owners)
      // Link force to calculate link distances and strengths
      .force('link', d3.forceLink(data.graph).id(d => d.name))
      // Y force to position nodes along the y-axis, with higher strength for the first node to be at the bottom
      .force('y', d3.forceY().y((d) => d.name === rootNode ? D3GraphSetting.svgHeight - D3GraphSetting.nodeHeight : null).strength(0.5))
      // X force to position nodes along the x-axis, with higher strength for the hidden node to be at the right
      .force('x', d3.forceX().x((d) => d.type === 'hidden' ? 1000 : null).strength(0.1))
      // Collide force to prevent nodes from overlapping by applying collision detection
      .force('collide', d3.forceCollide().radius(D3GraphSetting.nodeWidth / 2 + D3GraphSetting.elementPadding).strength(1));

    // Remove previous SVGs that are created due to React change detection and the append svg code to the DOM
    d3.select(`#${D3GraphSetting.ContainerID} svg`).remove();

    // Create the current SVG element
    const svg = d3.select(`#${D3GraphSetting.ContainerID}`)
      .append('svg')
      .attr('width', '100%')
      .attr('height', D3GraphSetting.svgHeight);

    // Define the arrowhead marker
    svg.append('defs').append('marker')
      .attr('id', 'arrowhead')
      .attr('viewBox', '0 -5 10 10')
      .attr('refX', 10)
      .attr('refY', 0)
      .attr('markerWidth', 10)
      .attr('markerHeight', 10)
      .attr('orient', 'auto-start-reverse')
      .append('path')
      .attr('d', 'M 0 -5 L 10 0 L 0 5')
      .style('fill', D3GraphSetting.strokeColor); // Add fill color for arrowhead (optional)

    // Create a group for all elements to allow translation
    const g = svg.append('g');

    // Create the links and the nodes
    // The links first so that they are under the nodes when drawn
    createLinks(g);
    createNodes(g, rootNode);

    let currentTransform = d3.zoomIdentity;

    // Zoom functionality (includes drag)
    const zoom = d3.zoom()
      .on('zoom', (event) => {
        currentTransform = event.transform;
        g.attr('transform', currentTransform);
      });

    svg.call(zoom);

    // Node drag functionality
    // Create a drag behavior
    const drag = d3.drag()
      .on('start', (event, d) => {
        // Change the initial load setting so that it becomes static
        setInitialLoad(false);

        // Fix the node of all nodes on node drag
        fixNodePositions(data.owners);

        if (!event.active) simulation.alphaTarget(0.3).restart(); // Restart simulation on drag start
        d.fx = d.x; // Fix the current x position of the node
        d.fy = d.y; // Fix the current y position of the node
      })
      .on('drag', (event, d) => {
        d.fx = event.x; // Update fixed x position of the node based on drag event
        d.fy = event.y; // Update fixed y position of the node based on drag event
      })
      .on('end', (event, d) => {
        if (!event.active) simulation.alphaTarget(0); // Stop simulation on drag end
      });

    // Apply event listeners to nodes
    g.selectAll('.node')
      .call(drag); // Attach the drag behavior

    // Update node and link positions
    simulation.on('tick', () => {
      // Select elements
      const link = g.selectAll('.link');
      const node = g.selectAll('.node');

      // Update position of path (cubic Bezier curve)
      link.select('.link-path')
        .attr('d', (d) => {
          // Offset added due output and input arrows mixing otherwise
          // With the offset we get a classic input/output visualization regardless on which side it is
          const offsetX = 5;
          const sourceX = d.source.x + offsetX;
          let sourceY = d.source.y;
          const targetX = d.target.x - offsetX;
          let targetY = d.target.y;
          let sourceYOffset = targetY >= sourceY ? -10 : 10;
          let targetYOffset = targetY >= sourceY ? 10 : -10;

          // Cover the cases of circular ownership with an ellipse
          if (sourceX - offsetX === targetX + offsetX && sourceY === targetY) {
            const radius = D3GraphSetting.nodeWidth / (2 * 2) + 10;
            return `M${sourceX},${sourceY - D3GraphSetting.nodeHeight / 2} ` + // Move to the starting point (top of the node)
              `L${sourceX},${sourceY - 35} ` + // Straight line from above
              `A${radius},${radius} 0 1,1 ${sourceX},${sourceY + radius / 2} ` + // Arc around the node
              `M${sourceX},${sourceY + D3GraphSetting.nodeHeight / 2}` + // Move to the finishing point (bottom of the node)
              `L${sourceX},${sourceY + 30} `; // Straight line from below
          }

          // Set offsets based on node height
          if (sourceY > targetY) {
            sourceY = d.source.y - D3GraphSetting.nodeHeight / 2;
            targetY = d.target.y + D3GraphSetting.nodeHeight / 2;
          } else {
            sourceY = d.source.y + D3GraphSetting.nodeHeight / 2;
            targetY = d.target.y - D3GraphSetting.nodeHeight / 2;
          }

          const controlX1 = sourceX;
          const controlY1 = sourceY + (targetY - sourceY) * 0.5 - D3GraphSetting.nodeHeight / 2;
          const controlX2 = targetX;
          const controlY2 = targetY - (targetY - sourceY) * 0.5 + D3GraphSetting.nodeHeight / 2;

          return `M${sourceX},${sourceY} ` +
            `L${sourceX},${sourceY - sourceYOffset} ` + // straight line out of source
            `C${controlX1},${controlY1} ${controlX2},${controlY2} ${targetX},${targetY - targetYOffset}` +
            `L${targetX},${targetY}`; // straight line into target point
        });

      link.select('.link-rect')
        .attr('x', d => (d.source.x + d.target.x) / 2 - 25) // Adjust width and height as needed
        .attr('y', d => (d.source.y + d.target.y) / 2 - 12); // Adjust y-offset as needed

      // Update position and content of text along the path
      link.select('.link-text')
        .attr('x', d => {
          // We want to show the link text to the right for self ownership
          // unlike the others which are centered
          if (d.source.name === d.target.name) {
            return (d.source.x + D3GraphSetting.nodeWidth / 2 + 35);
          }

          return (d.source.x + d.target.x) / 2;
        })
        .attr('y', d => (d.source.y + d.target.y) / 2);

      // Update position of nodes
      node.attr('transform', d => `translate(${d.x},${d.y})`);

      // The values required for initial and zoom translation and scaling
      // Need to be set on tick of the simulation since the size is changing
      updateContainerSize(svg, g, currentTransform);
    });
  }, [data, createLinks, createNodes, updateContainerSize, setInitialLoad]);

  return <div style={{ border: '1px solid #E4E7F0' }} id={D3GraphSetting.ContainerID}></div>;
};

export default OrganizationalChart;
